import { useSignal } from '@preact/signals-react';
import TextEdit from '../../molecules/TextEdit';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { useNavigate } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import Tab from '../../molecules/Tab';
import useFetch from '../../../hooks/useFetch';
import ToggleSwitch from '../../atoms/ToogleSwitch';

const DEFAULT_ACCOUNT_ID = '111257797516';

const buildLayerLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/lambda/home?region=${region}#/layers/${name}?accountId=${account}`;
const buildTableLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/dynamodb/home?region=${region}#tables:selected=${name};tab=overview?accountId=${account}`;
const buildScheduleLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/events/home?region=${region}#/schedules/${name}?accountId=${account}`;

const CONFIGURATION_URL = `${process.env.REACT_APP_COMMUNICATION_API_URL}/config/page-view`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

const CommunicationConfiguration: React.FC = () => {
    const navigate = useNavigate();
    const tabIndex = useSignal(0);

    const { isLoading, data, error, isTokenError } = useFetch(CONFIGURATION_URL, [
        { key: 'x-api-key', value: API_KEY },
    ]);

    const region = data?.region || 'us-east-1';
    const accountId = data?.accountId || DEFAULT_ACCOUNT_ID;

    if (isTokenError) {
        navigate('/denied');
    }

    if (error) {
        navigate('/network-error');
    }

    const renderListTable = (items: any[]) => (
        <ATable headers={[]} autoResize={false} marginTop={0}>
            {items.map((item, index) => (
                <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300`}>
                    <td className="py-2 px-2 whitespace-nowrap">{String(item)}</td>
                </tr>
            ))}
        </ATable>
    );

    const renderKeyValueTable = (obj: Record<string, unknown>) => {
        const headers: ATableHeader[] = [
            { id: 'key', title: 'Chave', canSort: false, width: '200px' },
            { id: 'value', title: 'Valor', canSort: false },
        ];
        return (
            <ATable headers={headers} autoResize={false} marginTop={0}>
                {Object.entries(obj).map(([key, value], index) => (
                    <tr key={key} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300`}>
                        <td className="py-2 px-2 whitespace-nowrap">{key}</td>
                        <td className="py-2 px-2">{String(value)}</td>
                    </tr>
                ))}
            </ATable>
        );
    };

    const renderAdapter = () => {
        if (!data) return null;
        const adapters: Record<string, any> = {};
        data.adapters?.forEach((adapter: any) => {
            adapters[adapter.type.toUpperCase()] = adapter.data;
        });
        if (tabIndex.value === 0) {
            const ses = adapters.SES || {};
            const keys = Object.keys(ses).filter((k) => Array.isArray(ses[k]));
            const [allowKey, blockKey] = keys;
            return (
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white border rounded-lg p-6 shadow overflow-y-auto max-h-48">
                        <h4 className="font-semibold mb-2">{allowKey}</h4>
                        {renderListTable(ses[allowKey] || [])}
                    </div>
                    <div className="bg-white border rounded-lg p-6 shadow overflow-y-auto max-h-48">
                        <h4 className="font-semibold mb-2">{blockKey}</h4>
                        {renderListTable(ses[blockKey] || [])}
                    </div>
                </div>
            );
        }
        if (tabIndex.value === 1) return renderKeyValueTable(adapters.BLIP || {});
        return renderKeyValueTable(adapters.SLACK || {});
    };

    const renderParams = () => {
        if (!data) return null;
        const fields: [string, unknown][] = [];
        const tables: [string, any[]][] = [];

        const mainTable = data.params.notification_table_name || '';
        const auxTable = data.params.misc_table_name || '';
        return (
            <>
                <div className="grid grid-cols-12 gap-4">

                    <div className="col-span-6">
                        <label className="block text-sm text-gray-500 mb-1">Tabela principal:</label>
                        <a
                            href={buildTableLink(mainTable, region, accountId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                        >
                            {mainTable}
                        </a>
                    </div>

                    <div className="col-span-6">
                        <label className="block text-sm text-gray-500 mb-1">Tabela auxiliar</label>
                        <a
                            href={buildTableLink(auxTable, region, accountId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                        >
                            {auxTable}
                        </a>
                    </div>

                </div>
                {tables.map(([key, items]) => (
                    <div key={key} className="mt-4 overflow-x-auto">
                        <h4 className="font-semibold mb-2">{key}</h4>
                        {renderListTable(items)}
                    </div>
                ))}
                {renderSchedules()}
            </>
        );
    };

    const renderSchedules = () => {
        if (!data) return null;
        return (
            <div className="mt-4">
                <h4 className="font-semibold mb-2">Schedules</h4>
                <ul className="space-y-2">
                    {data.schedules?.map((schedule: any) => (
                        <li key={schedule.name} className="flex items-center justify-between">
                            <a
                                href={buildScheduleLink(schedule.name, region, accountId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                            >
                                {schedule.name}
                            </a>
                            <ToggleSwitch
                                checked={schedule.state === 'ENABLED'}
                                onChange={() => { }}
                                disabled
                                activeClass="bg-green-500"
                                inactiveClass="bg-gray-300"
                            />
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderLayers = () => {
        if (!data) return null;
        return (
            <div className="space-y-2">
                {data.layers?.map((layer: any) => (
                    <details key={layer.name} className="border rounded-lg">
                        <summary className="cursor-pointer px-4 py-2 flex justify-between items-center">
                            <a
                                href={buildLayerLink(layer.name, region, accountId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-blue-600 hover:underline"
                            >
                                {layer.name}
                            </a>
                        </summary>
                        <div className="p-4 border-t overflow-x-auto">
                            {Array.isArray(layer.env_vars)
                                ? renderListTable(layer.env_vars)
                                : renderKeyValueTable(layer.env_vars || {})}
                        </div>
                    </details>
                ))}
            </div>
        );
    };

    return (
        <PageForm
            pageTitle="Configuração - Comunicação"
            apiVersion={data ? `${data.apiName} - ${data.apiVersion}` : ''}
            isLoading={isLoading}
            breadcrumb={[{ label: 'Configurações - comunicação', url: '#' }]}
            controlSlot={<></>}
        >
            <div className="container mx-auto py-4">
                <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className="col-span-12 md:col-span-6 bg-white border rounded-lg p-6 shadow">
                        <h3 className="font-semibold mb-2">Configurações gerais</h3>
                        {renderParams()}
                    </div>
                    <div className="col-span-12 md:col-span-6 bg-white border rounded-lg p-6 shadow">
                        <h3 className="font-semibold mb-2">Camadas da aplicação</h3>
                        {renderLayers()}
                    </div>
                </div>
                <div className="bg-white border rounded-lg p-6 shadow">
                    <h3 className="font-semibold mb-4">Adaptadores</h3>
                    <Tab
                        tabItems={[
                            { id: 'ses', title: 'SES', index: 0 },
                            { id: 'blip', title: 'BLIP', index: 1 },
                            { id: 'slack', title: 'Slack', index: 2 },
                        ]}
                        tabIndex={tabIndex.value}
                        setTabIndex={(index) => (tabIndex.value = index)}
                    />
                    <div className="mt-4">{renderAdapter()}</div>
                </div>
            </div>
        </PageForm>
    );
};

export default CommunicationConfiguration;
